// import { useState } from 'react';
// import { Box, Button, Card, TextField, Typography, LinearProgress, Alert } from '@mui/material';
// import { Lock, Key, CheckCircle, Save, Clear, Shield } from '@mui/icons-material';

// export default function Setting() {
//   const [pwd, setPwd] = useState('');
//   const strength = pwd.length < 6 ? 0 : pwd.length < 10 ? 60 : 100;

//   return (
//     <Card sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
//       <Typography variant="h6" mb={2} display="flex" alignItems="center" gap={1}>
//         <Key /> Security Settings
//       </Typography>

//       <Box component="form" noValidate autoComplete="off">
//         <TextField
//           fullWidth
//           type="password"
//           label="Current Password"
//           margin="normal"
//           InputProps={{ startAdornment: <Lock color="primary" sx={{ mr: 1 }} /> }}
//         />
//         <TextField
//           fullWidth
//           type="password"
//           label="New Password"
//           margin="normal"
//           value={pwd}
//           onChange={(e) => setPwd(e.target.value)}
//           InputProps={{ startAdornment: <Key color="primary" sx={{ mr: 1 }} /> }}
//         />
//       </Box>

//       <LinearProgress
//         variant="determinate"
//         value={strength}
//         sx={{ height: 4, borderRadius: 2, mb: 2 }}
//         color={strength < 60 ? 'error' : strength < 100 ? 'warning' : 'success'}
//       />

//       <TextField
//         fullWidth
//         type="password"
//         label="Confirm New Password"
//         margin="normal"
//         InputProps={{ startAdornment: <CheckCircle color="primary" sx={{ mr: 1 }} /> }}
//       />

//       <Box display="flex" gap={2} mt={3}>
//         <Button variant="contained" startIcon={<Save />}>Update Password</Button>
//         <Button variant="outlined" startIcon={<Clear />}>Cancel</Button>
//       </Box>

//       <Alert icon={<Shield />} severity="info" sx={{ mt: 3 }}>
//         Use a strong password with letters, numbers & symbols.
//       </Alert>
//     </Card>
//   );
// }





import { useState } from 'react';
import {
    Box,
    Card,
    TextField,
    Button,
    Typography,
    Divider,
    Snackbar,
    Alert,
    CircularProgress,
    LinearProgress,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axiosInstance from '../../../services/axiosInterceptors';
import apiEndPointsConfig from '../../../services/apiEndPoints';
import { Lock, Key, CheckCircle, Save } from '@mui/icons-material';
import { palette, theme as colorTheme } from '../../../Convoze.theme';

const primary = colorTheme('palette', 'primary').value;
const positive = colorTheme('palette', 'positive').value;
const negative = colorTheme('palette', 'negative').value;


export default function SettingsTab() {
    const token = localStorage.getItem('token');
    const [open, setOpen] = useState(false);
    const [msg, setMsg] = useState('');
    const [sev, setSev] = useState('error');
    const [loading, setLoading] = useState(false);

    /* ---------- same validation ---------- */
    const validationSchema = Yup.object({
        currentPassword: Yup.string()
            .required('Current password is required')
            .matches(/^\S+$/, 'Password cannot contain spaces'),
        newPassword: Yup.string()
            .required('New password is required')
            .min(6, 'Password must be at least 6 characters')
            .matches(/^\S+$/, 'Password cannot contain spaces'),
        confirmPassword: Yup.string()
            .required('Please confirm your password')
            .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
            .matches(/^\S+$/, 'Password cannot contain spaces'),
    });

    /* ---------- same submit ---------- */
    const formik = useFormik({
        initialValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
        validationSchema,
        onSubmit: async (values) => {
            setLoading(true);
            try {
                await axiosInstance.put(
                    apiEndPointsConfig.changePassword,
                    { oldPassword: values.currentPassword, newPassword: values.newPassword, confirmPassword: values.confirmPassword },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setSev('success');
                setMsg('Password changed successfully');
                formik.resetForm();
            } catch (err) {
                const val = err?.response?.data?.status?.value;
                if (val === 'Old password is incorrect') setMsg('Old password given is incorrect');
                else if (val === 'New password must be different from the old password')
                    setMsg('New password must be different from the old one');
                else setMsg('Something went wrong');
                setSev('error');
            } finally {
                setLoading(false);
                setOpen(true);
            }
        },
    });

    /* ---------- strength bar ---------- */
    const strength =
        formik.values.newPassword.length === 0
            ? 0
            : formik.values.newPassword.length < 6
                ? 40
                : formik.values.newPassword.length < 10
                    ? 70
                    : 100;

    return (
        <Card
            sx={{
                p: { xs: 2, md: 4 },
                maxWidth: 640,
                mx: 'auto',
                border: 1,
                borderColor: 'grey.200',
                borderRadius: 3,
                boxShadow: 0,
            }}
        >
            <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Key sx={{ color: primary }} />
                <Typography variant="h6" fontWeight={600}>
                    Security
                </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" mb={3}>
                Update your password to keep your account secure
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <form onSubmit={formik.handleSubmit}>
                {/* Current */}
                <Typography variant="body2" fontWeight={500} mb={1}>
                    Current Password
                </Typography>
                <TextField
                    fullWidth
                    type="password"
                    name="currentPassword"
                    value={formik.values.currentPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter current password"
                    error={formik.touched.currentPassword && Boolean(formik.errors.currentPassword)}
                    helperText={formik.touched.currentPassword && formik.errors.currentPassword}
                    InputProps={{ startAdornment: <Lock sx={{ mr: 1, color: primary }} /> }}
                    sx={{
                        mb: 2.5,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: primary, borderWidth: 2 },
                        },
                    }}
                />

                {/* New */}
                <Typography variant="body2" fontWeight={500} mb={1}>
                    New Password
                </Typography>
                <TextField
                    fullWidth
                    type="password"
                    name="newPassword"
                    value={formik.values.newPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Create a strong password"
                    error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
                    helperText={formik.touched.newPassword && formik.errors.newPassword}
                    InputProps={{ startAdornment: <Key sx={{ mr: 1, color: primary }} /> }}
                    sx={{
                        mb: 1,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: primary, borderWidth: 2 },
                        },
                    }}
                />
                <LinearProgress
                    variant="determinate"
                    value={strength}
                    sx={{ height: 4, borderRadius: 2, mb: 2.5 }}
                    color={strength < 70 ? negative : positive}
                />

                {/* Confirm */}
                <Typography variant="body2" fontWeight={500} mb={1}>
                    Confirm New Password
                </Typography>
                <TextField
                    fullWidth
                    type="password"
                    name="confirmPassword"
                    value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Re-enter new password"
                    error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                    helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                    InputProps={{ startAdornment: <CheckCircle sx={{ mr: 1, color: primary }} /> }}
                    sx={{
                        mb: 3,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: primary, borderWidth: 2 },
                        },
                    }}
                />

                {/* Actions */}
                <Box display="flex" gap={2} justifyContent="flex-end">
                    <Button
                        variant="outlined"
                        onClick={() => formik.resetForm()}
                        sx={{ borderRadius: 2, textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        sx={{
                            bgcolor: primary,
                            color: '#fff',
                            px: 3,
                            borderRadius: 2,
                            textTransform: 'none',
                            '&:hover': { bgcolor: primary, opacity: 0.9 },
                        }}
                    >
                        {loading ? 'Updating…' : 'Update Password'}
                    </Button>
                </Box>
            </form>

            <Snackbar
                open={open}
                autoHideDuration={4000}
                onClose={() => setOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={() => setOpen(false)} severity={sev} sx={{ width: '100%' }}>
                    {msg}
                </Alert>
            </Snackbar>
        </Card>
    );
}